import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, Clock, CheckCircle2, XCircle, Loader2, DollarSign, RefreshCw, Download } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import StatCard from '../../components/StatCard';
import ConfirmModal from '../../components/ConfirmModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import * as payoutsApi from '../../api/payouts.api';
import { errorMessage } from '../../api/client';
import { formatMoney, formatDateTime } from '../../utils/format';

const STATUS_TONE = {
  PENDING:    'warning',
  APPROVED:   'info',
  PROCESSING: 'brand',
  PAID:       'success',
  FAILED:     'danger',
  REJECTED:   'danger',
  CANCELLED:  'neutral',
};

function StatusPill({ status }) {
  return <StatusBadge tone={STATUS_TONE[status] || 'neutral'}>{status}</StatusBadge>;
}

function AdminPayouts() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [action, setAction] = useState(null); // { kind: 'release'|'reject'|'approve', row }
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    setErr('');
    try {
      const [list, sum] = await Promise.all([
        payoutsApi.listAdminPayouts(statusFilter ? { status: statusFilter } : {}),
        payoutsApi.adminPayoutSummary(),
      ]);
      setRows(list || []);
      setSummary(sum || null);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await load();
      if (cancelled) return;
      // Silently sync any PROCESSING payouts in the background — covers
      // the case where Curlec's webhook was delayed or missed. Runs on
      // every list load so stuck rows self-heal without admin action.
      const list = (await payoutsApi.listAdminPayouts(statusFilter ? { status: statusFilter } : {})).filter?.(
        (p) => p.status === 'PROCESSING',
      ) || [];
      for (const p of list.slice(0, 20)) {
        try { await payoutsApi.syncPayoutStatus(p._id); } catch { /* ignore */ }
      }
      if (!cancelled && list.length > 0) await load();
    })();
    return () => { cancelled = true; };
  }, [statusFilter]);

  const totals = summary?.totals || {};
  const counts = summary?.counts || {};
  const owed = useMemo(
    () => (totals.PENDING || 0) + (totals.APPROVED || 0),
    [totals]
  );

  async function submit() {
    if (!action) return;
    setBusy(true);
    try {
      if (action.kind === 'release')  await payoutsApi.releasePayout(action.row._id, note);
      if (action.kind === 'approve')  await payoutsApi.approvePayout(action.row._id, note);
      if (action.kind === 'reject')   await payoutsApi.rejectPayout(action.row._id, note || 'Rejected by admin');
      setAction(null);
      setNote('');
      await load();
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function syncOne(row) {
    try {
      await payoutsApi.syncPayoutStatus(row._id);
      await load();
    } catch (e) {
      setErr(errorMessage(e));
    }
  }

  async function downloadCsv() {
    try {
      const blob = await payoutsApi.exportAdminPayoutsCsv(statusFilter ? { status: statusFilter } : {});
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payouts-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(errorMessage(e));
    }
  }

  if (loading && !rows.length) return <LoadingSpinner />;

  return (
    <>
      <PageHeader
        title="Payouts"
        subtitle="Money owed to clinics from completed customer bookings. Auto-created on service confirmation."
        actions={
          <button className="btn btn-ghost" onClick={downloadCsv}>
            <Download size={16} /> Export CSV
          </button>
        }
      />

      <div className="grid grid-4" style={{ marginBottom: 'var(--s-4)' }}>
        <StatCard icon={Clock}       tone="warning" label="Pending"    value={counts.PENDING || 0}    deltaLabel={formatMoney(totals.PENDING || 0)} />
        <StatCard icon={Loader2}     tone="brand"   label="Processing" value={counts.PROCESSING || 0} deltaLabel={formatMoney(totals.PROCESSING || 0)} />
        <StatCard icon={CheckCircle2} tone="success" label="Paid"       value={counts.PAID || 0}       deltaLabel={formatMoney(totals.PAID || 0)} />
        <StatCard icon={Wallet}      tone="brand"   label="Total owed" value={formatMoney(owed)}      deltaLabel="Pending + Approved" />
      </div>

      <DataTable
        loading={loading}
        rows={rows}
        searchPlaceholder="Search by clinic, order, batch id…"
        toolbarLeft={
          <select
            className="input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ maxWidth: 200 }}
          >
            <option value="">All statuses</option>
            {['PENDING','APPROVED','PROCESSING','PAID','FAILED','REJECTED','CANCELLED'].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        }
        columns={[
          {
            key: 'clinic', header: 'Clinic',
            accessor: (r) => r.clinic?.clinicProfile?.clinicName
                          || r.bankSnapshot?.accountHolderName
                          || r.clinic?.name || '',
            render: (r) => {
              // Prefer the clinic's legal/trading name; then the account-holder
              // name saved with the payout (guaranteed accurate — it's exactly
              // what Curlec was told to pay); then the user's own name (which
              // for test data is often the contact person like "John Doe").
              const primary = r.clinic?.clinicProfile?.clinicName
                           || r.bankSnapshot?.accountHolderName
                           || r.clinic?.name
                           || '—';
              const sub = r.clinic?.clinicProfile?.clinicName
                ? (r.bankSnapshot?.accountHolderName || r.clinic?.email)
                : r.clinic?.email;
              return (
                <div style={{ minWidth: 0 }}>
                  <div className="cell-strong cell-truncate">{primary}</div>
                  <div className="muted text-xs cell-truncate">{sub}</div>
                </div>
              );
            }
          },
          {
            key: 'order', header: 'Order',
            accessor: (r) => r.order?.orderNumber || '',
            render: (r) => r.order?._id
              ? <Link className="cell-mono text-sm" to={`/orders/${r.order._id}`}>{r.order.orderNumber || r.order.bookingId || '—'}</Link>
              : '—'
          },
          {
            key: 'gross', header: 'Gross',
            render: (r) => <span className="muted">{formatMoney(r.grossAmount, r.currency)}</span>
          },
          {
            key: 'pct', header: '%',
            render: (r) => <span className="badge neutral">{r.clinicSharePct}%</span>
          },
          {
            key: 'clinicAmount', header: 'To clinic',
            render: (r) => <span className="cell-strong">{formatMoney(r.clinicAmount, r.currency)}</span>
          },
          {
            key: 'platformAmount', header: 'Platform',
            render: (r) => <span className="muted">{formatMoney(r.platformAmount, r.currency)}</span>
          },
          {
            key: 'status', header: 'Status',
            render: (r) => <StatusPill status={r.status} />
          },
          {
            key: 'createdAt', header: 'Created',
            render: (r) => <span className="muted">{formatDateTime(r.createdAt)}</span>
          },
          {
            key: 'actions', header: '',
            render: (r) => (
              <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                {r.status === 'PENDING' && (
                  <button className="btn btn-ghost btn-sm" onClick={() => { setAction({ kind: 'approve', row: r }); setNote(''); }}>
                    Approve
                  </button>
                )}
                {(r.status === 'PENDING' || r.status === 'APPROVED' || r.status === 'FAILED') && (
                  <button className="btn btn-primary btn-sm" onClick={() => { setAction({ kind: 'release', row: r }); setNote(''); }}>
                    <DollarSign size={14} /> Release
                  </button>
                )}
                {r.status === 'PROCESSING' && (
                  <button
                    className="btn btn-ghost btn-sm"
                    title="Refresh status from Curlec"
                    onClick={() => syncOne(r)}
                  >
                    <RefreshCw size={14} /> Sync
                  </button>
                )}
                {(r.status === 'PENDING' || r.status === 'APPROVED') && (
                  <button className="btn btn-danger btn-sm" onClick={() => { setAction({ kind: 'reject', row: r }); setNote(''); }}>
                    <XCircle size={14} /> Reject
                  </button>
                )}
              </div>
            )
          }
        ]}
        emptyTitle="No payouts yet"
        emptyMessage="Payouts appear here after customers confirm receipt of a service."
      />

      {err && <div className="notice warning" style={{ marginTop: 'var(--s-4)' }}>{err}</div>}

      <ConfirmModal
        open={Boolean(action)}
        title={
          action?.kind === 'release' ? 'Release payout to clinic?' :
          action?.kind === 'approve' ? 'Approve payout?' :
          action?.kind === 'reject'  ? 'Reject payout?' : ''
        }
        message={
          action ? (
            <div>
              <p>
                {action.kind === 'release'
                  ? (() => {
                      const clinicName = action.row.clinic?.clinicProfile?.clinicName
                                      || action.row.bankSnapshot?.accountHolderName
                                      || action.row.clinic?.name
                                      || 'this clinic';
                      return `Send ${formatMoney(action.row.clinicAmount, action.row.currency)} to ${clinicName} via Curlec Payouts.`;
                    })()
                  : action.kind === 'approve'
                  ? `Mark this payout as approved (no funds move yet).`
                  : `This payout will not be paid. Provide a reason for the clinic.`}
              </p>
              <label className="form-label" style={{ marginTop: 8 }}>
                {action.kind === 'reject' ? 'Reason' : 'Note (optional)'}
              </label>
              <textarea
                className="input"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={action.kind === 'reject' ? 'Explain why…' : 'Add a note…'}
              />
            </div>
          ) : null
        }
        confirmLabel={
          action?.kind === 'release' ? 'Release now' :
          action?.kind === 'approve' ? 'Approve' :
          action?.kind === 'reject'  ? 'Reject payout' : 'Confirm'
        }
        tone={action?.kind === 'reject' ? 'danger' : 'primary'}
        loading={busy}
        onCancel={() => { if (!busy) { setAction(null); setNote(''); } }}
        onConfirm={submit}
      />
    </>
  );
}

export default AdminPayouts;
